<template>
  <div>
    <!-- Tabs -->
    <div class="tabs flex border-b mb-4">
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'lab-request',
          'text-gray-500': currentTab !== 'lab-request',
        }"
        @click="currentTab = 'lab-request'"
      >
        Lab Request
      </button>
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'radiology-request',
          'text-gray-500': currentTab !== 'radiology-request',
        }"
        @click="currentTab = 'radiology-request'"
      >
        Radiology Request
      </button>
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'procedure-request',
          'text-gray-500': currentTab !== 'procedure-request',
        }"
        @click="currentTab = 'procedure-request'"
      >
        Procedure Request
      </button>
    </div>

    <!-- Lab Request Form -->
    <div
      v-if="currentTab === 'lab-request' && (state === 'create' || state === 'edit')"
      class="grid grid-cols-1 gap-4 max-h-[65vh] overflow-y-auto"
    >
      <form class="space-y-4" @submit.prevent="submitLabRequestForm">
        <div>
          <!-- Quick Order Panel -->
          <div v-if="panels.length" class="flex flex-col px-3 mb-3 pb-3 border-b border-gray-200">
            <label class="font-semibold text-sm mb-2">Quick Order Panel</label>
            <div class="flex gap-2">
              <select v-model="panelToOrder" class="flex-1 border border-gray-300 rounded p-2 text-sm bg-gray-50">
                <option value="" disabled selected>— select a panel —</option>
                <option v-for="p in panels" :key="p.id" :value="p.id">
                  {{ p.name }} ({{ p.items.length }} tests)
                </option>
              </select>
              <button
                type="button"
                @click="orderPanel"
                :disabled="!panelToOrder"
                class="px-3 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-40"
              >
                Order All
              </button>
            </div>
          </div>

          <div class="flex flex-col px-3">
            <label class="font-semibold text-sm mt-2">
              <span class="text-red-500">* </span>Test Type
            </label>
            <div class="search-wrapper">
              <v-select
                class="border-gray-300 rounded p-3 mt-2 bg-gray-50"
                v-model="testRequest.labRequestForm.test_type"
                :options="labTests"
                :filterable="false"
                placeholder="Search for lab tests"
                @search="onSearch"
                @option:selected="labTestChange"
                label="name"
                :loading="isLoading"
              >
                <template #no-options="{ searching, loading }">
                  <template v-if="searching">
                    No results found for "{{ searching }}"
                  </template>
                  <template v-else-if="loading"> Loading results... </template>
                  <template v-else> Type to search for lab tests </template>
                </template>
                <template #option="option">
                  <div class="select-option">
                    <span>{{ option.name }}</span>
                    <small v-if="option.description">{{ option.description }}</small>
                  </div>
                </template>
                <template #selected-option="option">
                  <div class="selected-option">
                    {{ option.name }}
                  </div>
                </template>
              </v-select>
            </div>
          </div>
          <!-- Referred Out toggle -->
          <div class="flex flex-col px-3 mt-3">
            <label class="font-semibold text-sm mb-2">Test Location</label>
            <label class="inline-flex items-center gap-3 cursor-pointer select-none w-fit">
              <div
                class="relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer"
                :class="testRequest.labRequestForm.referred_out ? 'bg-custom-yellow' : 'bg-gray-300'"
                @click="testRequest.labRequestForm.referred_out = !testRequest.labRequestForm.referred_out"
              >
                <span
                  class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                  :class="testRequest.labRequestForm.referred_out ? 'translate-x-5' : 'translate-x-0'"
                />
              </div>
              <span class="text-sm font-medium text-gray-700">
                {{ testRequest.labRequestForm.referred_out ? 'Referred Out (external facility)' : 'In-house (billed internally)' }}
              </span>
            </label>
            <p v-if="testRequest.labRequestForm.referred_out" class="text-xs text-amber-600 mt-1 ml-1">
              No bill item will be created — the patient pays the external facility directly.
            </p>
          </div>
          <div class="flex flex-col px-3">
            <label class="font-semibold text-sm mt-2">
              <span class="text-red-500">* </span>Priority
            </label>
            <select
              :disabled="state === 'view'"
              v-model="testRequest.labRequestForm.priority"
              class="border-gray-300 rounded p-2 bg-gray-50 mt-2"
            >
              <option value="" disabled selected>
                Select (Normal, Urgent, STAT)
              </option>
              <option value="Normal">Normal</option>
              <option value="Urgent">Urgent</option>
              <option value="STAT">STAT</option>
            </select>
          </div>
          <div class="flex flex-col px-3">
            <label class="font-semibold text-sm mt-2">Additional Notes/Instructions</label>
            <textarea
              :disabled="state === 'view'"
              v-model="testRequest.labRequestForm.instructions"
              rows="2"
              class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
              placeholder="Type..."
            ></textarea>
          </div>
          <div class="flex flex-col px-3">
            <label class="font-semibold text-sm mt-2">Clinical Notes</label>
            <textarea
              :disabled="state === 'view'"
              v-model="testRequest.labRequestForm.clinical_notes"
              rows="2"
              class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
              placeholder="Type..."
            ></textarea>
          </div>
          <div v-if="testRequest.labRequestForm.unit_price" class="flex flex-col px-3">
            <label class="font-semibold text-sm mt-2">Lab Test Cost</label>
            <br />
            <span>KES {{ testRequest.labRequestForm.unit_price }}</span>
          </div>
        </div>
        <div class="flex justify-end px-2">
          <button
            type="button"
            @click="addLabRequest"
            :disabled="state === 'view'"
            :class="[
              'py-1 px-4 rounded',
              state === 'view'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-custom-yellow text-white hover:bg-custom-blue',
            ]"
          >
            Add Lab Request
          </button>
        </div>
      </form>
      <!-- Lab Requests Table -->
      <div v-if="labRequests.length > 0" class="mt-2 px-2">
        <h2 class="text-lg font-semibold mb-4">Lab Requests</h2>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Test Type
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Priority
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Cost
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Location
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(labRequest, index) in labRequests" :key="index">
              <td class="px-6 py-1 whitespace-nowrap">
                {{ labRequest.test_type?.name }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                {{ labRequest.priority ? labRequest.priority : labRequest.queue_priority }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                Ksh. {{ labRequest.test_type?.unit_price }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="labRequest.referred_out ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'"
                >
                  {{ labRequest.referred_out ? 'Referred Out' : 'In-house' }}
                </span>
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                <button
                  v-if="!labRequest.id"
                  @click="deleteLabRequest(index)"
                  class="text-gray-50 bg-red-500 hover:text-gray-600 px-4 py-1 rounded-md right-0"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-if="currentTab === 'lab-request' && state === 'view'"
      class="grid grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto"
    >
      <div v-if="existingLabTests.length > 0">
        <div v-for="(test, index) in existingLabTests" :key="index">
          <div>
            <p class="font-semibold">Test Type:</p>
            <p>{{ test?.test_type?.name || "-" }}</p>
          </div>
          <div>
            <p class="font-semibold">Requesting Doctor:</p>
            <p>{{ test?.requested_by?.Username || "-" }}</p>
          </div>
        </div>
      </div>
      <div v-else>No Lab Test(s) requested.</div>
    </div>

    <!-- Radiology Request Form -->
    <div
      v-if="currentTab === 'radiology-request' && (state === 'create' || state === 'edit')"
      class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto"
    >
      <form class="space-y-4" @submit.prevent="submitRadiologyRequestForm">
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">
            <span class="text-red-500">* </span>Date
          </label>
          <div class="relative">
            <input
              :disabled="state === 'view'"
              v-model="testRequest.radiologyForm.date"
              type="date"
              class="border-gray-300 rounded p-2 mt-1 bg-gray-50 w-full"
            />
          </div>
        </div>
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">
            <span class="text-red-500">* </span>Imaging Type
          </label>
          <div class="search-wrapper">
            <v-select
              v-model="testRequest.radiologyForm.imaging_type"
              :options="radiologyTests"
              :filterable="false"
              placeholder="Search for radiology tests"
              @option:selected="radiologyTestChange"
              @search="onSearch"
              :loading="isLoading"
            >
              <template #no-options="{ searching, loading }">
                <template v-if="searching">
                  No results found for "{{ searching }}"
                </template>
                <template v-else-if="loading">
                  Loading radiology tests...
                </template>
                <template v-else> Type to search radiology tests </template>
              </template>
              <template #option="option">
                <div class="select-option">
                  <span>{{ option.name }}</span>
                  <small v-if="option.description">{{ option.description }}</small>
                </div>
              </template>
              <template #selected-option="option">
                <div class="selected-option">
                  {{ option.name }}
                </div>
              </template>
            </v-select>
          </div>
        </div>
        <!-- Referred Out toggle -->
        <div class="flex flex-col px-3 mt-3">
          <label class="font-semibold text-sm mb-2">Test Location</label>
          <label class="inline-flex items-center gap-3 cursor-pointer select-none w-fit">
            <div
              class="relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer"
              :class="testRequest.radiologyForm.referred_out ? 'bg-custom-yellow' : 'bg-gray-300'"
              @click="testRequest.radiologyForm.referred_out = !testRequest.radiologyForm.referred_out"
            >
              <span
                class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                :class="testRequest.radiologyForm.referred_out ? 'translate-x-5' : 'translate-x-0'"
              />
            </div>
            <span class="text-sm font-medium text-gray-700">
              {{ testRequest.radiologyForm.referred_out ? 'Referred Out (external facility)' : 'In-house (billed internally)' }}
            </span>
          </label>
          <p v-if="testRequest.radiologyForm.referred_out" class="text-xs text-amber-600 mt-1 ml-1">
            No bill item will be created — the patient pays the external facility directly.
          </p>
        </div>
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">
            <span class="text-red-500">* </span>Body Part/Region
          </label>
          <input
            :disabled="state === 'view'"
            v-model="testRequest.radiologyForm.bodyPartRegion"
            type="text"
            placeholder="Type..."
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
        </div>
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">
            <span class="text-red-500">* </span>Priority
          </label>
          <select
            :disabled="state === 'view'"
            v-model="testRequest.radiologyForm.priority"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          >
            <option value="" disabled selected>
              Select (Normal, Urgent, STAT)
            </option>
            <option value="Normal">Normal</option>
            <option value="Urgent">Urgent</option>
            <option value="STAT">STAT</option>
          </select>
        </div>
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">Clinical Notes</label>
          <textarea
            :disabled="state === 'view'"
            v-model="testRequest.radiologyForm.clinical_notes"
            rows="4"
            class="w-full border-gray-300 rounded p-2 mt-1 bg-gray-50"
            placeholder="Type..."
          ></textarea>
        </div>
        <div v-if="testRequest.radiologyForm.unit_price" class="flex flex-col px-3">
          <label class="font-semibold text-sm mt-2">Radiology Test Cost</label>
          <br />
          <span>KES {{ testRequest.radiologyForm.unit_price }}</span>
        </div>
        <div class="flex justify-end px-2">
          <button
            type="button"
            @click="addRadiologyRequest"
            :disabled="state === 'view'"
            :class="[
              'py-1 px-4 rounded',
              state === 'view'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-custom-yellow text-white hover:bg-custom-blue',
            ]"
          >
            Add Radiology Request
          </button>
        </div>
      </form>
      <!-- Radiology Requests Table -->
      <div v-if="radiologyRequests?.length > 0" class="mt-2 px-2">
        <h2 class="text-lg font-semibold mb-4">Radiology Requests</h2>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Imaging Type
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Priority
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Cost
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500">
                Location
              </th>
              <th class="px-6 py-2 text-left text-md font-medium text-gray-500"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(radiologyRequest, index) in radiologyRequests" :key="index">
              <td class="px-6 py-1 whitespace-nowrap">
                {{ radiologyRequest.imaging_type?.name || "-" }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                {{ radiologyRequest.priority ? radiologyRequest.priority : radiologyRequest.queue_priority || "-" }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                Ksh. {{ radiologyRequest.imaging_type?.unit_price || "-" }}
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                <span
                  class="text-xs px-2 py-0.5 rounded-full font-medium"
                  :class="radiologyRequest.referred_out ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'"
                >
                  {{ radiologyRequest.referred_out ? 'Referred Out' : 'In-house' }}
                </span>
              </td>
              <td class="px-6 py-1 whitespace-nowrap">
                <button
                  v-if="!radiologyRequest.id"
                  @click="deleteRadiologyRequest(index)"
                  class="text-gray-50 bg-red-500 hover:text-gray-600 px-4 py-1 rounded-md right-0"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-if="currentTab === 'radiology-request' && state === 'view'"
      class="grid grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto"
    >
      <div v-if="existingRadiologyTests.length > 0">
        <div v-for="(test, index) in existingRadiologyTests" :key="index">
          <div>
            <p class="font-semibold">Imaging Type:</p>
            <p>{{ test?.imaging_type?.name || "-" }}</p>
          </div>
          <div>
            <p class="font-semibold">Body Part/Region:</p>
            <p>{{ test?.bodyPartRegion || "-" }}</p>
          </div>
          <div>
            <p class="font-semibold">Requesting Doctor:</p>
            <p>{{ test?.requested_by?.Username || "-" }}</p>
          </div>
        </div>
      </div>
      <div v-else>No Radiology Test(s) requested.</div>
    </div>

    <!-- ── Procedure Request Form ── -->
    <div
      v-if="currentTab === 'procedure-request' && (state === 'create' || state === 'edit')"
      class="grid grid-cols-1 gap-4 max-h-[65vh] overflow-y-auto"
    >
      <form class="space-y-4" @submit.prevent>
        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm mt-2">
            <span class="text-red-500">* </span>Procedure / Service
          </label>
          <v-select
            class="border-gray-300 rounded p-1 mt-2 bg-gray-50"
            v-model="procedureForm.procedure_type"
            :options="procedureServices"
            :filterable="false"
            placeholder="Search for a procedure or service"
            @search="onProcedureSearch"
            @option:selected="procedureServiceChange"
            label="name"
            :loading="isProcedureLoading"
          >
            <template #no-options="{ searching, loading }">
              <template v-if="searching">No results found for "{{ searching }}"</template>
              <template v-else-if="loading">Loading...</template>
              <template v-else>Type to search procedures</template>
            </template>
            <template #option="option">
              <div>
                <span>{{ option.name }}</span>
                <small v-if="option.unit_price" class="ml-2 text-gray-400">KES {{ option.unit_price }}</small>
              </div>
            </template>
            <template #selected-option="option">{{ option.name }}</template>
          </v-select>
        </div>

        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">Quantity</label>
          <input
            :disabled="state === 'view'"
            v-model.number="procedureForm.quantity"
            type="number"
            min="1"
            placeholder="1"
            class="border-gray-300 rounded p-2 mt-1 bg-gray-50"
          />
        </div>

        <div class="flex flex-col px-3">
          <label class="font-semibold text-sm">Notes / Instructions</label>
          <textarea
            :disabled="state === 'view'"
            v-model="procedureForm.notes"
            rows="3"
            placeholder="Any special instructions..."
            class="w-full border-gray-300 rounded p-2 mt-1 bg-gray-50"
          ></textarea>
        </div>

        <div v-if="procedureForm.unit_price" class="flex flex-col px-3">
          <label class="font-semibold text-sm">Cost</label>
          <span>KES {{ procedureForm.unit_price }}</span>
        </div>

        <div class="flex justify-end px-2">
          <button
            type="button"
            @click="addProcedureRequest"
            :disabled="state === 'view'"
            :class="[
              'py-1 px-4 rounded',
              state === 'view'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-custom-yellow text-white hover:bg-custom-blue',
            ]"
          >
            Add Procedure
          </button>
        </div>
      </form>

      <!-- Added procedures table -->
      <div v-if="procedureRequests.length > 0" class="mt-2 px-2">
        <h2 class="text-lg font-semibold mb-4">Requested Procedures</h2>
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-2 text-left text-sm font-medium text-gray-500">Procedure</th>
              <th class="px-6 py-2 text-left text-sm font-medium text-gray-500">Qty</th>
              <th class="px-6 py-2 text-left text-sm font-medium text-gray-500">Cost</th>
              <th class="px-6 py-2 text-left text-sm font-medium text-gray-500"></th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(proc, index) in procedureRequests" :key="index">
              <td class="px-6 py-1">{{ proc.procedure_type?.name || "-" }}</td>
              <td class="px-6 py-1">{{ proc.quantity }}</td>
              <td class="px-6 py-1">KES {{ proc.unit_price }}</td>
              <td class="px-6 py-1">
                <button
                  @click="deleteProcedureRequest(index)"
                  class="text-gray-50 bg-red-500 hover:bg-red-600 px-4 py-1 rounded-md"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Procedure view mode -->
    <div
      v-if="currentTab === 'procedure-request' && state === 'view'"
      class="px-3 py-2 text-sm text-gray-500"
    >
      Procedure requests for this visit are managed from the Procedures queue.
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import apiClient from "@/services/api";
import "vue-select/dist/vue-select.css";

export default {
  components: {
    vSelect,
  },
  props: {
    state: {
      type: String,
      required: true,
    },
    visitId: {
      type: String,
      required: true,
    },
    existingLabTests: {
      type: Array,
      default: () => [],
    },
    existingRadiologyTests: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentTab: "lab-request",
      panels: [],
      panelToOrder: null,
      testRequest: {
        labRequestForm: {
          test_type: "",
          priority: "Normal",
          instructions: "",
          quantity: 1,
          bill_category: 2,
          clinical_notes: "",
          unit_price: 0,
          referred_out: false,
        },
        radiologyForm: {
          date: this.getTodayDate(),
          scheduleRequest: "",
          imaging_type: "",
          bodyPartRegion: "",
          priority: "Normal",
          quantity: 1,
          bill_category: 3,
          clinical_notes: "",
          unit_price: 0,
          referred_out: false,
        },
      },
      labRequests: [],
      radiologyRequests: [],
      procedureRequests: [],
      labTests: [],
      radiologyTests: [],
      procedureServices: [],
      isLoading: false,
      isProcedureLoading: false,
      searchTimeout: null,
      procedureSearchTimeout: null,
      minChars: 3,
      procedureForm: {
        procedure_type: null,
        quantity: 1,
        notes: "",
        unit_price: 0,
        bill_category: 5,
      },
    };
  },
  async created() {
    await this.loadPanels();
  },
  watch: {
    existingLabTests: {
      handler(newItems) {
        this.labRequests = [...newItems];
      },
      immediate: true,
      deep: true,
    },
    existingRadiologyTests: {
      handler(newItems) {
        this.radiologyRequests = [...newItems];
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async loadPanels() {
      try {
        const res = await apiClient.listLabPanels();
        if (res.success) this.panels = res.data;
      } catch { /* non-critical */ }
    },
    orderPanel() {
      if (!this.panelToOrder) return;
      const panel = this.panels.find(p => p.id === this.panelToOrder);
      if (!panel) return;
      panel.items.forEach(item => {
        const alreadyAdded = this.labRequests.some(r => r.test_type?.id === item.facility_service_id);
        if (!alreadyAdded) {
          this.labRequests.push({
            test_type:      { id: item.facility_service_id, name: item.service_name, unit_price: item.unit_price },
            priority:       "Normal",
            instructions:   "",
            quantity:       1,
            bill_category:  2,
            clinical_notes: "",
            unit_price:     item.unit_price,
            referred_out:   false,
          });
        }
      });
      this.panelToOrder = null;
      this.$toast.success(`${panel.items.length} test(s) added from "${panel.name}"`);
    },
    labTestChange(event) {
      this.testRequest.labRequestForm.unit_price = event.unit_price;
    },
    radiologyTestChange(event) {
      this.testRequest.radiologyForm.unit_price = event.unit_price;
    },
    async onSearch(search, loading) {
      if (search.length < this.minChars) {
        return;
      }
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.isLoading = true;
      loading(true);
      this.searchTimeout = setTimeout(async () => {
        try {
          await this.searchService(search);
        } catch (error) {
          console.error("Search error:", error);
          this.labTests = [];
        } finally {
          this.isLoading = false;
          loading(false);
        }
      }, 300);
    },
    async searchService(searchString) {
      try {
        if (searchString.length < 3) return;
        this.isLoading = true;
        const type = this.currentTab === "radiology-request" ? "FST-003" : "FST-002";
        const payload = { name: searchString, type: type };
        const response = await apiClient.searchConsultationFacilityServices(payload);
        if (response.success) {
          if (this.currentTab === "radiology-request") {
            this.radiologyTests = response.data;
          } else {
            this.labTests = response.data;
          }
        } else {
          this.$toast.error("Unable to search tests.");
        }
      } catch (error) {
        this.$toast.error("Unable to search tests, please check your internet connection.");
      } finally {
        this.isLoading = false;
      }
    },
    onProcedureSearch(search, loading) {
      if (search.length < this.minChars) return;
      if (this.procedureSearchTimeout) clearTimeout(this.procedureSearchTimeout);
      this.isProcedureLoading = true;
      loading(true);
      this.procedureSearchTimeout = setTimeout(async () => {
        try {
          const response = await apiClient.searchConsultationFacilityServices({ name: search, type: "FST-004" });
          if (response.success) this.procedureServices = response.data;
        } catch {
          this.$toast.error("Unable to search procedures.");
        } finally {
          this.isProcedureLoading = false;
          loading(false);
        }
      }, 300);
    },
    procedureServiceChange(service) {
      this.procedureForm.unit_price = service.unit_price;
    },
    addLabRequest() {
      if (
        this.testRequest.labRequestForm.test_type &&
        this.testRequest.labRequestForm.priority
      ) {
        this.labRequests.push({ ...this.testRequest.labRequestForm });
        this.resetLabRequestForm();
      } else {
        alert("Please fill all required fields in the Lab Request.");
      }
    },
    deleteLabRequest(index) {
      this.labRequests.splice(index, 1);
    },
    addRadiologyRequest() {
      if (
        this.testRequest.radiologyForm.date &&
        this.testRequest.radiologyForm.imaging_type &&
        this.testRequest.radiologyForm.priority &&
        this.testRequest.radiologyForm.bodyPartRegion
      ) {
        this.radiologyRequests.push({ ...this.testRequest.radiologyForm });
        this.resetRadiologyRequestForm();
      } else {
        alert("Please fill all required fields in the Radiology Request.");
      }
    },
    deleteRadiologyRequest(index) {
      this.radiologyRequests.splice(index, 1);
    },
    addProcedureRequest() {
      if (!this.procedureForm.procedure_type) {
        this.$toast.error("Please select a procedure or service.");
        return;
      }
      this.procedureRequests.push({ ...this.procedureForm });
      this.resetProcedureRequestForm();
    },
    deleteProcedureRequest(index) {
      this.procedureRequests.splice(index, 1);
    },
    resetProcedureRequestForm() {
      this.procedureForm = { procedure_type: null, quantity: 1, notes: "", unit_price: 0, bill_category: 5 };
    },
    resetLabRequestForm() {
      this.testRequest.labRequestForm = {
        test_type: "",
        priority: "Normal",
        instructions: "",
        quantity: 1,
        bill_category: 1,
        clinical_notes: "",
        unit_price: 0,
        referred_out: false,
      };
    },
    resetRadiologyRequestForm() {
      this.testRequest.radiologyForm = {
        date: this.getTodayDate(),
        imaging_type: "",
        bodyPartRegion: "",
        priority: "Normal",
        quantity: 1,
        bill_category: 2,
        clinical_notes: "",
        unit_price: 0,
        referred_out: false,
      };
    },
    submitAllRequests() {
      return {
        labRequests:       this.labRequests,
        radiologyRequests: this.radiologyRequests,
        procedureRequests: this.procedureRequests,
      };
    },
    resetAllForms() {
      this.resetLabRequestForm();
      this.resetRadiologyRequestForm();
      this.resetProcedureRequestForm();
      this.labRequests       = [];
      this.radiologyRequests = [];
      this.procedureRequests = [];
    },
  },
};
</script>

<style scoped>
/* Add your styles here */
</style>
